import '../payment-overdue/index.css'
import React, { useEffect, useRef } from 'react'
import { MdError as ErrorIcon } from '@react-icons/all-files/md/MdError'
import { connect, type ConnectedProps } from 'react-redux'
import { Link } from 'react-router-dom'
import type { BusinessData } from 'types/business'
import { loadBusinessData } from '@disco/state-management/actions/UserActions'
import { getCurrentQuota } from '@disco/utils/pricing'
const POLLING_INTERVAL = 2 * 60 * 1000 // 2 minutes

type OverquotaInfo = null | {
  tracks?: number
  users?: number
}
export function isOverquota(
  business: BusinessData | null | undefined,
): OverquotaInfo {
  if (!(business?.private_metadata)) return null
  const { enable_over_quota_banner } = business.private_metadata
  if (!enable_over_quota_banner) return null
  const { total_track_count, number_of_users } = business.stats
  const { maxTracks, maxUsers } = getCurrentQuota(business)
  const deltaTracks = total_track_count - maxTracks
  const deltaUsers = number_of_users - maxUsers
  const overquota: OverquotaInfo = {}
  if (deltaTracks > 0) overquota.tracks = deltaTracks
  if (deltaUsers > 0) overquota.users = deltaUsers
  if (Object.keys(overquota).length === 0) return null
  return overquota
}

function useInterval(callback: () => void, delay: number | null) {
  const savedCallback = useRef<() => void>(() => {})
  useEffect(() => {
    savedCallback.current = callback
  }, [callback])
  useEffect(() => {
    function tick() {
      savedCallback.current()
    }

    if (delay !== null) {
      let id = setInterval(tick, delay)
      return () => clearInterval(id)
    }
  }, [delay])
}

const connector = connect(null, { loadBusinessData })

type ReduxProps = ConnectedProps<typeof connector>
interface Props extends ReduxProps {
  overquotaInfo: OverquotaInfo
}

function PaymentOverdueBanner(props: Props) {
  const { overquotaInfo, loadBusinessData } = props
  useInterval(() => {
    loadBusinessData()
  }, POLLING_INTERVAL)
  if (!overquotaInfo) return null
  let { users, tracks } = overquotaInfo
  let xUsers = users ? `${users} ${users !== 1 ? 'users' : 'user'}` : ''
  let xTracks = tracks ? `${tracks} ${tracks !== 1 ? 'tracks' : 'track'}` : ''
  let msg
  let removeTheAdditionalUsers = (
    <Link to="/settings/users">remove the additional users</Link>
  )

  if (users && tracks) {
    msg = (
      <span>
        You’re currently over the user and track allowance for your plan. Please{' '}
        <Link to="/settings/billing">upgrade</Link> your plan, or{' '}
        {removeTheAdditionalUsers} and tracks.
      </span>
    )
  } else if (users) {
    msg = (
      <span>
        You’re currently {xUsers} over the allowance for your plan. Please{' '}
        <Link to="/settings/billing">add more seats</Link> to your plan, or{' '}
        {removeTheAdditionalUsers}.
      </span>
    )
  } else if (tracks) {
    msg = (
      <span>
        You’re currently {xTracks} over the allowance for your plan. Please{' '}
        <Link to="/settings/billing">upgrade</Link> your plan, or remove the
        additional tracks.
      </span>
    )
  }

  return (
    <div className="payment-overdue">
      <ErrorIcon
        width="1.5em"
        height="1.5em"
        color="#fff"
        style={{
          margin: '0 1rem 0 1.3rem',
        }}
      />
      {msg}
    </div>
  )
}

export default connector(PaymentOverdueBanner)
